import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import AdminTable from '../components/AdminTable';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { getAdminEnquiries, deleteEnquiry } from '../api/apiService';

const AdminEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [open, setOpen] = useState(false);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');
  const [filterType, setFilterType] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const sortStr = order === 'desc' ? `-${orderBy}` : orderBy;
      const data = await getAdminEnquiries(page + 1, rowsPerPage, searchQuery, sortStr, filterType, dateFilter);
      setEnquiries(Array.isArray(data.data) ? data.data : []);
      setTotalCount(data.total || 0);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, [page, rowsPerPage, searchQuery, orderBy, order, filterType, dateFilter]);

  const handleFilter = (type, value) => {
    if (type === 'category') setFilterType(value);
    if (type === 'date') setDateFilter(value);
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const columns = [
    { id: 'name', label: 'Name' },
    { id: 'email', label: 'Email' },
    { id: 'formType', label: 'Type' },
    {
      id: 'status',
      label: 'Status',
      render: (value) => (
        <Chip
          label={value}
          size="small"
          color={value === 'resolved' ? 'success' : value === 'pending' ? 'warning' : 'info'}
          variant="outlined"
          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
        />
      ),
    },
    { id: 'createdAt', label: 'Date', render: (val) => new Date(val).toLocaleDateString() },
  ];

  const handleOpen = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedEnquiry(null);
  };

  const handleDeleteClick = (enquiry) => {
    setEnquiryToDelete(enquiry);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (enquiryToDelete) {
      try {
        await deleteEnquiry(enquiryToDelete.id);
        fetchEnquiries();
        setDeleteDialogOpen(false);
        setEnquiryToDelete(null);
      } catch (error) {
        console.error('Error deleting enquiry:', error);
      }
    }
  };

  return (
    <Box>
      <AdminTable
        title="Enquiries Management"
        columns={columns}
        data={enquiries}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        loading={loading}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        onSearch={setSearchQuery}
        onSort={handleSort}
        onFilter={handleFilter}
        categories={['General', 'Feature']}
        onView={handleOpen}
        onDelete={handleDeleteClick}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: '16px' } } }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>Enquiry Details</Typography>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEnquiry && (
            <Stack spacing={2.5} sx={{ py: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">NAME</Typography>
                <Typography variant="body1" fontWeight={600}>{selectedEnquiry.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">EMAIL</Typography>
                <Typography variant="body1">{selectedEnquiry.email}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">TYPE</Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{selectedEnquiry.formType}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>MESSAGE</Typography>
                <Typography variant="body2" sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: '8px', lineHeight: 1.6 }}>
                  {selectedEnquiry.message}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleClose} sx={{ color: '#666', textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Enquiry?"
        message={`Are you sure you want to delete the enquiry from "${enquiryToDelete?.name}"? This action cannot be undone.`}
      />
    </Box>
  );
};

export default AdminEnquiries;
